import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LabelId, UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useDeleteSelectionShortcut } from './useDeleteSelectionShortcut';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import {
  useMarkAsReadUserCommunication,
  useMarkAsUnreadUserCommunication,
  useMoveToInboxUserCommunication,
  useReportAsSpamUserCommunication,
} from '../UserCommunications';
import { useCustomToast } from '../useCustomToast';

interface UseInboxAllActionsProps {
  filteredCommunications: UserCommunication[];
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

export function useInboxAllActions({
  filteredCommunications,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseInboxAllActionsProps) {
  const { registerShortcut, unregisterShortcut, currentContext } =
    useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();

  const { handleMoveToInboxUserCommunication } =
    useMoveToInboxUserCommunication();
  const { handleMarkAsReadUserCommunication } =
    useMarkAsReadUserCommunication();
  const { handleMarkAsUnreadUserCommunication } =
    useMarkAsUnreadUserCommunication();
  const { handleReportAsSpamUserCommunication } =
    useReportAsSpamUserCommunication();
  const deleteHandler = useDeleteSelectionShortcut({
    filteredCommunications,
    selectedItems,
    selectedEmailIndex,
    clearSelection,
  });

  useEffect(() => {
    const ctx = [ShortcutContext.ALL_MAIL];
    unregisterShortcut(ShortcutKey.SELECT, ctx);
    unregisterShortcut(ShortcutKey.MOVE_TO_INBOX, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
    unregisterShortcut(ShortcutKey.REPORT_SPAM, ctx);
    unregisterShortcut(ShortcutKey.DELETE, ctx);
    unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
    unregisterShortcut(ShortcutKey.BACKSPACE, ctx);

    registerShortcut({
      key: ShortcutKey.SELECT,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: () => {
        if (
          selectedEmailIndex >= 0 &&
          selectedEmailIndex < filteredCommunications.length
        ) {
          const selectedEmail = filteredCommunications[selectedEmailIndex];
          if (selectedEmail) {
            toggleItemSelection(selectedEmail.messageId);
          }
        }
      },
      description: 'Select/deselect current email',
    });

    const moveToInboxHandler = () => {
      if (selectedItems.size > 0) {
        const selectedCommunications = filteredCommunications.filter((comm) =>
          selectedItems.has(comm.messageId),
        );
        const hasSentItems = selectedCommunications.some((comm) =>
          comm.labelIds?.includes(LabelId.SENT),
        );
        if (hasSentItems) {
          return;
        }

        const itemsArray = Array.from(selectedItems);

        clearSelection?.();
        promiseToast(
          handleMoveToInboxUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.movingToInbox'),
          t('inbox.userCommunication.actions.moveToInboxMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.moveToInboxError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          if (selectedEmail.labelIds?.includes(LabelId.SENT)) {
            return;
          }

          handleMoveToInboxUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MOVE_TO_INBOX,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: moveToInboxHandler,
      description: 'Move selected email(s) to inbox',
    });

    const markAsReadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        promiseToast(
          handleMarkAsReadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsRead'),
          t('inbox.userCommunication.actions.markAsReadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsReadError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleMarkAsReadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_READ,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: markAsReadHandler,
      description: 'Mark selected email(s) as read',
    });

    const markAsUnreadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);

        promiseToast(
          handleMarkAsUnreadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsUnread'),
          t('inbox.userCommunication.actions.markAsUnreadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsUnreadError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleMarkAsUnreadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_UNREAD,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: markAsUnreadHandler,
      description: 'Mark selected email(s) as unread',
      shiftKey: true,
    });

    const reportSpamHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleReportAsSpamUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.reportingAsSpam'),
          t('inbox.userCommunication.actions.reportAsSpamMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.reportAsSpamError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleReportAsSpamUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.REPORT_SPAM,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: reportSpamHandler,
      description: 'Report selected email(s) as spam',
    });

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.ALL_MAIL],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    return () => {
      unregisterShortcut(ShortcutKey.SELECT, ctx);
      unregisterShortcut(ShortcutKey.MOVE_TO_INBOX, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
      unregisterShortcut(ShortcutKey.REPORT_SPAM, ctx);
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleMoveToInboxUserCommunication,
    handleMarkAsReadUserCommunication,
    handleMarkAsUnreadUserCommunication,
    handleReportAsSpamUserCommunication,
    deleteHandler,
    currentContext,
  ]);
}
